import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Alert, Autocomplete, Box, Button, Chip, IconButton, List, ListItem, ListItemText, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { listPlugins } from '../../lib/payPolicyTemplates/api';
import LoadMoreFooter from './LoadMoreFooter';
import LoadOnScroll from './LoadOnScroll';
import SearchField from './SearchField';
import { startPluginDrag, usePluginDropZone } from './pluginDrag';
import { versionChoices } from './templateDraft';
import { CatalogPlugin } from './types';
import { SEARCH_RULES, useCursorList } from './useCursorList';

interface PluginCatalogProps {
  /** The template's own plugins, each with the version range the template holds it at. */
  selected: Map<string, string>;
  onAdd: (plugin: CatalogPlugin, version: string) => void;
  onRemove: (pluginId: string) => void;
  /** Why a plugin cannot be taken off the template, or null when it can. */
  removeBlockedBy?: (pluginId: string) => string | null;
  /** Plugins Global Calculations loads for the template's own ones. */
  autoLoaded: Set<string>;
  /** Browsable only: no adding, removing, dragging or picking versions. */
  disabled?: boolean;
}

interface VersionPickerProps {
  plugin: CatalogPlugin;
  value: string;
  onChange: (version: string) => void;
  disabled: boolean;
}

/** Every version of the plugin, newest first, as the range it is stored with; typing narrows the list. */
const VersionPicker = ({ plugin, value, onChange, disabled }: VersionPickerProps) => {
  const choices = versionChoices(plugin.versions, value);
  // Held here rather than left to Autocomplete, which does not always show its first value.
  const [typed, setTyped] = useState(value);
  useEffect(() => setTyped(value), [value]);
  return (
    <Autocomplete
      size="small"
      disableClearable
      autoHighlight
      disabled={disabled}
      options={choices}
      value={value}
      inputValue={typed}
      onInputChange={(_, next, reason) => setTyped(reason === 'reset' ? value : next)}
      onClose={() => setTyped(value)}
      onChange={(_, next) => onChange(next)}
      filterOptions={(options) => (typed === value ? options : options.filter((option) => option.includes(typed.trim())))}
      sx={{ width: 150 }}
      onMouseDown={(event) => event.stopPropagation()}
      renderInput={(params) => <TextField {...params} variant="standard" inputProps={{ ...params.inputProps, 'aria-label': `Version of ${plugin.id}` }} />}
      ListboxProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
    />
  );
};

/**
 * The version picker only chooses what + adds; the template changes only when a plugin is added or
 * removed. A row of a plugin the template holds offers − while its picker shows the version held,
 * and + otherwise, which the editor answers by saying a template holds one version of a plugin.
 */
const PluginCatalog = ({ selected, autoLoaded, onAdd, onRemove, removeBlockedBy = () => null, disabled = false }: PluginCatalogProps) => {
  const [search, setSearch] = useState('');
  // The version picked on a row; until then, the one the template holds, else the newest.
  const [picked, setPicked] = useState<Record<string, string>>({});
  const { items, total, loading, error, hasMore, autoLoad, loadMore, retry } = useCursorList(listPlugins, search, SEARCH_RULES.text);
  const shown = (plugin: CatalogPlugin) => picked[plugin.id] ?? selected.get(plugin.id) ?? versionChoices(plugin.versions)[0];
  // Once off the template, the row offers the newest again rather than the version just removed.
  const remove = (pluginId: string) => {
    setPicked(({ [pluginId]: _, ...rest }) => rest);
    onRemove(pluginId);
  };
  // A drag from the template names no version: it takes off whatever the template holds.
  const dropZone = usePluginDropZone((drag) => {
    if (!disabled && drag.origin === 'template' && !removeBlockedBy(drag.pluginId)) remove(drag.pluginId);
  });

  return (
    <Box {...dropZone.handlers} sx={{ borderRadius: 1, outline: dropZone.active ? '2px dashed' : 'none', outlineColor: 'error.main', outlineOffset: 4 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Plugin catalog
      </Typography>
      <SearchField label="Search plugins" value={search} onChange={setSearch} rule={SEARCH_RULES.text} tooShort="Type at least 3 characters" fullWidth />
      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={retry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      <List dense disablePadding sx={{ maxHeight: 560, overflowY: 'auto', overscrollBehavior: 'contain' }}>
        {items.map((plugin) => {
          const templateRange = selected.get(plugin.id);
          const inTemplate = templateRange !== undefined;
          const offersRemove = inTemplate && shown(plugin) === templateRange;
          const blocked = inTemplate ? removeBlockedBy(plugin.id) : null;
          return (
            <ListItem
              key={plugin.id}
              divider
              draggable={!disabled}
              onDragStart={(event) => startPluginDrag(event, { origin: 'catalog', plugin, version: shown(plugin) })}
              sx={{ bgcolor: inTemplate ? 'action.selected' : undefined, cursor: disabled ? 'default' : 'grab' }}
              secondaryAction={
                offersRemove ? (
                  <Tooltip title={blocked ?? `Remove ${templateRange} from the template`}>
                    <span>
                      <IconButton edge="end" aria-label={`Remove ${plugin.id}`} disabled={disabled || blocked !== null} onClick={() => remove(plugin.id)}>
                        <RemoveCircleOutlineIcon color={disabled || blocked ? 'disabled' : 'error'} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip title={disabled ? '' : `Add ${shown(plugin)} to the template, with the plugins it needs (or drag it there)`}>
                    <span>
                      <IconButton edge="end" aria-label={`Add ${plugin.id}`} disabled={disabled} onClick={() => onAdd(plugin, shown(plugin))}>
                        <AddCircleOutlineIcon color={disabled ? 'disabled' : 'primary'} />
                      </IconButton>
                    </span>
                  </Tooltip>
                )
              }
            >
              <ListItemText
                primary={plugin.id}
                secondary={
                  <>
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip component="span" size="small" variant="outlined" label={plugin.family} />
                      {!inTemplate && autoLoaded.has(plugin.id) && (
                        <Tooltip title="Loaded automatically for a plugin of this template">
                          <Chip component="span" size="small" color="info" variant="outlined" label="auto" />
                        </Tooltip>
                      )}
                      <VersionPicker plugin={plugin} value={shown(plugin)} disabled={disabled} onChange={(version) => setPicked((previous) => ({ ...previous, [plugin.id]: version }))} />
                    </Box>
                  </>
                }
                primaryTypographyProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                secondaryTypographyProps={{ component: 'div' }}
              />
            </ListItem>
          );
        })}
        {!loading && items.length === 0 && !error && (
          <ListItem>
            <ListItemText secondary="No plugins found" />
          </ListItem>
        )}
        <LoadOnScroll active={autoLoad} onLoad={loadMore} />
      </List>
      <LoadMoreFooter loaded={items.length} total={total} loading={loading} hasMore={hasMore} onLoadMore={loadMore} size="small" />
    </Box>
  );
};

export default PluginCatalog;
